import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { demo, StatusBadge, SyntheticTag } from "./c1lib";
import { Section } from "./PublicLayout";
import { ArrowRight, BadgeCheck, XCircle } from "lucide-react";

export default function VerifiedRecords() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { demo.list().then(setRows).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const live = rows.filter((r) => r.is_live);
  const others = rows.filter((r) => !r.is_live);

  return (
    <Section title="Verified Records" subtitle="Live production records appear first, clearly separated from the reference demonstration records that show deterministic Certified and Withheld outcomes.">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-3">Live records</h3>
      <div className="border border-slate-200 bg-white divide-y divide-slate-100 mb-10" data-testid="live-records-list">
        {live.length === 0 && <div className="p-6 text-slate-400 text-sm">No live records yet.</div>}
        {live.map((r) => (
          <RecordRow key={r.record_id} r={r} live />
        ))}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <h3 className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Reference / demonstration records</h3>
        <SyntheticTag />
      </div>
      <div className="flex flex-wrap gap-3 mb-4" data-testid="records-shortcuts">
        <Link to="/records/PRM-DEMOCERT01" className="inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-sm px-4 py-2 text-sm"><BadgeCheck size={15} /> Certified reference</Link>
        <Link to="/records/PRM-DEMOHOLD02" className="inline-flex items-center gap-2 border border-slate-300 bg-white text-slate-700 rounded-sm px-4 py-2 text-sm"><XCircle size={15} /> Withheld reference</Link>
      </div>
      <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="records-list">
        {others.length === 0 && <div className="p-8 text-slate-400 text-sm">No records yet.</div>}
        {others.map((r) => (
          <RecordRow key={r.record_id} r={r} />
        ))}
      </div>
    </Section>
  );
}

function RecordRow({ r, live }) {
  return (
    <Link to={`/records/${r.record_id}`} data-testid={`record-row-${r.record_id}`}
      className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-slate-600 bg-slate-100 px-1.5 rounded-sm">{r.record_id}</span>
          {live
            ? <span className="font-mono text-[9px] uppercase tracking-[0.18em] border border-emerald-300 bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded-sm">Live</span>
            : r.is_reference && <SyntheticTag label="REFERENCE" />}
        </div>
        <div className="text-sm text-slate-800 mt-1 truncate">{r.seva_name} · <span className="text-slate-500">{r.devotee_name}</span></div>
      </div>
      <StatusBadge status={r.status} />
      <ArrowRight size={16} className="text-slate-300" />
    </Link>
  );
}
